import { ValidatorFn, AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import isEmail from 'validator/es/lib/isEmail';
import { ALPHA, ALPHA_WITH_SPACE } from './validation-patterns.const';
import { Validations } from './validations';
import { InputValidationNumbers } from './inputValidationNumbers.const';
import { DatePeriod } from '../date-period';
import { DateConst } from '../date.const';

export const CustomEmailValidator = () => Validator(isEmail, 'email');

export const AlphaValidator = (withSpace = true) =>
  RegexValidator(new RegExp(withSpace ? ALPHA_WITH_SPACE : ALPHA), 'alpha');

export const GroupValidator =
  (
    firstControlName: string,
    secondControlName: string,
    isValidFn: (firstControlValue: string, secondControlValue: string) => boolean,
    error: string,
  ) =>
  (group: AbstractControl): ValidationErrors | null => {
    const firstControl = group.get(firstControlName);
    const secondControl = group.get(secondControlName);
    if (!firstControl || !secondControl) return null;
    const forbidden = !isValidFn(firstControl?.value, secondControl?.value);
    if (forbidden) {
      firstControl?.setErrors({
        ...(firstControl.errors ?? {}),
        [error]: { value: `${firstControl?.value} - ${secondControl?.value}` },
      });
      firstControl.markAsTouched({ emitEvent: false });
      return { [error]: { value: `${firstControl?.value} - ${secondControl?.value}` } };
    }
    if (group.get([firstControlName])?.hasError(error)) {
      removeValidatorError(group.get([firstControlName]), error);
    }
    return null;
  };

export const startEndValidator = GroupValidator(
  'start',
  'end',
  (start: string, end: string) => !start || !end || +start <= +end,
  'range',
);

const Validator =
  (validatorFn: (...args: any[]) => boolean, error: string): ValidatorFn =>
  (abstractControl: AbstractControl): ValidationErrors | null => {
    const forbidden = !validatorFn(abstractControl.value ? abstractControl.value : '');
    return forbidden ? { [error]: { value: abstractControl.value } } : null;
  };

const RegexValidator = (regex: RegExp, error: string): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const valid = regex.test(control.value);
    return valid ? null : { [error]: { value: control.value } };
  };
};

export const PhoneWrapperValidator = () => (control: AbstractControl) => {
  const minlength = InputValidationNumbers.inputPhoneMin;
  const maxlength = InputValidationNumbers.inputPhoneMax;
  const prefix = control.get('prefix')?.value;
  const phone = control.get('phone')?.value;
  const phoneWrapper = `${prefix}${phone}`;

  if (phoneWrapper.length < minlength) {
    return { [Validations.minlength]: { requiredLength: minlength } };
  } else if (phoneWrapper.length > maxlength) {
    return { [Validations.maxlength]: { requiredLength: maxlength } };
  }

  return null;
};

export const EqualValuesValidator = (controlName1: string, controlName2: string) => (control: AbstractControl) => {
  const value1 = control.get(controlName1)?.value;
  const value2 = control.get(controlName2)?.value;

  if (value1 === value2) {
    return null;
  }

  return { valuesNotEqual: true };
};

export const fullNameTrimValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value as string;

  if (!value) return null;

  const startsOrEndsWithSpace = value.startsWith(' ') || value.endsWith(' ');
  const invalidCharacters = !/^[a-zA-Z0-9'’-]+(?: [a-zA-Z0-9'’-]+)*$/.test(value);

  if (startsOrEndsWithSpace) {
    return { fullNameTrim: true };
  }

  if (invalidCharacters) {
    return { fullNameInvalidChars: true };
  }

  return null;
};

export const trimValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value as string;

  if (!value) return null;

  const withSpace = value.replace(/\s/g, '');

  if (!withSpace) {
    return { trim: true };
  }

  return null;
};

export const futureDateAndBirthValidator: (birth: string) => ValidatorFn =
  (birth: string) =>
  (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const date = +DateConst.getYear(control.value.toString());
    if (date > +DateConst.getYear(new Date())) {
      control?.setErrors({ futureDate: true });
      return { futureDate: true };
    }
    if (date < +DateConst.getYear(birth)) {
      control?.setErrors({ futureDate: true });
      return { futureDate: true };
    }

    return null;
  };

export const futureDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) return null;
  const date = +DateConst.getYear(control.value.toString());
  if (date > +DateConst.getYear(new Date())) {
    control?.setErrors({ futureDate: true });
    return { futureDate: true };
  }

  return null;
};

export const futureFullDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) return null;
  const date = new Date(control.value.toString());
  if (date > new Date()) {
    control?.setErrors({ futureDate: true });
    return { futureDate: true };
  }

  return null;
};

export const livingYearsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const familyDateOfBirth = control.get('familyDateOfBirth')?.value;
  const familyDateOfPassing = control.get('familyDateOfPassing')?.value;
  const isOngoing = control.get('isOngoing')?.value;
  const years = control.get('years')?.value as DatePeriod;
  if (!years || !years.start) return null;
  if (!familyDateOfBirth) return null;
  const birth = +DateConst.getYear(familyDateOfBirth);
  const start = +DateConst.getYear(years.start.toString());
  if (birth > +start) {
    control.get('years.start')?.setErrors({ livingYears: true });
    return { livingYears: true };
  }
  if (!isOngoing && years.end) {
    const end = +DateConst.getYear(years.end.toString());
    if (birth > +end) {
      control.get('years.end')?.setErrors({ livingYears: true });
      return { livingYears: true };
    }
  }
  if (familyDateOfPassing) {
    const passing = +DateConst.getYear(familyDateOfPassing);
    if (passing < +start) {
      control.get('years.start')?.setErrors({ livingYears: true });
      return { livingYears: true };
    }
    if (!isOngoing && years.end) {
      const end = +DateConst.getYear(years.end.toString());
      if (passing < +end) {
        control.get('years.end')?.setErrors({ livingYears: true });
        return { livingYears: true };
      }
    }
    if (birth > passing) {
      control.get('familyDateOfPassing')?.setErrors({ livingYears: true });
      return { livingYears: true };
    }
  }

  removeValidatorError(control.get('familyDateOfPassing'), 'livingYears');
  removeValidatorError(control.get('years.start'), 'livingYears');
  removeValidatorError(control.get('years.end'), 'livingYears');
  return null;
};

export const passingDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const familyDateOfBirth = control.get('familyDateOfBirth')?.value;
  const familyDateOfPassing = control.get('familyDateOfPassing')?.value;
  if (!familyDateOfBirth || !familyDateOfPassing) return null;
  const birth = new Date(familyDateOfBirth);
  const passing = new Date(familyDateOfPassing);
  if (birth > passing) {
    control.get('familyDateOfPassing')?.setErrors({ passingDate: true });
    return { passingDate: true };
  }

  removeValidatorError(control.get('familyDateOfPassing'), 'passingDate');
  return null;
};

export const eitherDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const start = control.get('years.start')?.value;
  const end = control.get('years.end')?.value;
  const isOngoing = control.get('isOngoing')?.value;
  if (!isOngoing) {
    if (start && !end) {
      control.get('years.end')?.setErrors({
        ...(control.get('years.end')?.errors ?? {}),
        missingDate: true,
      });
      return { missingDate: true };
    }
    if (end && !start) {
      control.get('years.start')?.setErrors({
        ...(control.get('years.start')?.errors ?? {}),
        missingDate: true,
      });
      return { missingDate: true };
    }
  }
  removeValidatorError(control.get('years.start'), 'missingDate');
  removeValidatorError(control.get('years.end'), 'missingDate');
  return null;
};

const removeValidatorError = (control: AbstractControl | null, errorKey: string): void => {
  if (!control || !control.errors) return;

  const errors = { ...control.errors };
  delete errors[errorKey];

  control.setErrors(Object.keys(errors).length ? errors : null, { emitEvent: false });
};
